// src/pages/suppliers/ProductManagement.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Grid,
  Avatar,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Slide,
  InputAdornment,
  FormHelperText,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  alpha,
  Container,
  IconButton,
  Badge,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  Fade,
  Popover,
  styled,
  Stepper,
  Step,
  StepLabel,
  Skeleton,
  CircularProgress,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Verified as VerifiedIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  LockOutlined as LockOutlinedIcon,
  Notifications as NotificationsIcon,
  Image as ImageIcon,
  LocalShipping as LocalShippingIcon,
  Security as SecurityIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Science as ScienceIcon,
  Eco as EcoIcon,
  Shield as ShieldIcon,
  Warehouse as WarehouseIcon,
  Public as PublicIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { BRAND_COLORS } from '../buyers/components/styles';

// Add this import at the top of ProductManagement.jsx
import StockManagement from './StockManagement';

// ============================================
// API CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL ;

const API_ENDPOINTS = {
  PRODUCTS: {
    LIST: `${API_BASE_URL}/products/register.php`,
    CREATE: `${API_BASE_URL}/products/register.php`,
    UPDATE: (id) => `${API_BASE_URL}/products/register.php?id=${id}`,
    DELETE: (id) => `${API_BASE_URL}/products/register.php?id=${id}`,
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get full image URL from filename
 * @param {string} imagePath - The image filename
 * @returns {string|null} Full image URL or null
 */
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  return `${base}${imagePath}`;
};

/**
 * Format currency with proper symbol
 */
const formatCurrency = (amount, currency = 'NGN') => {
  if (!amount && amount !== 0) return 'N/A';
  const symbols = { NGN: '₦', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || currency;
  return `${symbol}${Number(amount).toLocaleString()}`;
};

/**
 * Truncate text with ellipsis
 */
const truncateText = (text, maxLength = 50) => {
  if (!text) return 'N/A';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

/**
 * Get HS Code from a product object, handling both naming conventions
 */
const getHsCode = (product) => {
  if (!product) return null;
  return product.hs_code || product.hsCode || product.HSCode || null;
};

// ============================================
// CONSTANTS
// ============================================

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const PRODUCT_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  DRAFT: 'draft',
};

const STATUS_COLORS = {
  [PRODUCT_STATUSES.ACTIVE]: BRAND_COLORS.success,
  [PRODUCT_STATUSES.INACTIVE]: BRAND_COLORS.error,
  [PRODUCT_STATUSES.PENDING]: BRAND_COLORS.warning,
  [PRODUCT_STATUSES.DRAFT]: BRAND_COLORS.info,
};

const STATUS_LABELS = {
  [PRODUCT_STATUSES.ACTIVE]: 'Active',
  [PRODUCT_STATUSES.INACTIVE]: 'Inactive',
  [PRODUCT_STATUSES.PENDING]: 'Pending',
  [PRODUCT_STATUSES.DRAFT]: 'Draft',
};

const APPLICATIONS = [
  'Food & Beverages',
  'Water Treatment',
  'Energy & Battery',
  'Electronics & Semiconductor',
  'Personal Care & Cosmetics',
  'Pharmaceuticals',
  'Agriculture',
  'Manufacturing',
];

const PRODUCT_TYPES = [
  'Solvents', 'Catalysts', 'Intermediates', 'Acids', 'Alkalis',
  'Corrosion Inhibitors', 'Surfactants', 'Pharmaceutical Ingredients',
  'Excipients', 'Diagnostic Reagents', 'Disinfectants', 'Electrolytes',
  'Fuel Additives',
];

const HAZARD_CLASSIFICATIONS = [
  'Flammable', 'Toxic', 'Corrosive', 'Reactive',
  'Explosives', 'Irritation', 'Oxidizing',
];

const GRADES = ['Industrial', 'Pharmaceutical', 'Food', 'Others'];
const CERTIFICATIONS = ['ISO', 'REACH', 'FDA', 'GMP', 'HACCP'];
const CURRENCIES = ['NGN', 'USD', 'EUR', 'GBP'];
const DELIVERY_TIMES = [
  '1-3 days',
  '4-7 days',
  '1-2 weeks',
  '2-3 weeks',
  '3-4 weeks',
  '1-2 months',
  '2-3 months',
  '3+ months',
];

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[PRODUCT-MGMT] ${message}`, data || ''),
  error: (message, error) => console.error(`[PRODUCT-MGMT] ${message}`, error || ''),
  debug: (message, data) => console.debug(`[PRODUCT-MGMT] ${message}`, data || ''),
  warn: (message, data) => console.warn(`[PRODUCT-MGMT] ${message}`, data || ''),
};

// ============================================
// STYLED COMPONENTS
// ============================================

const StyledCard = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  border: '1px solid #E5E7EB',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
  '&:hover': {
    boxShadow: `0 8px 40px ${alpha(BRAND_COLORS.primary, 0.06)}`,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.85rem',
  padding: '12px 16px',
  borderBottom: '1px solid #F0F2F5',
  '&.MuiTableCell-head': {
    backgroundColor: '#F8FAFC',
    color: BRAND_COLORS.text.secondary,
    fontWeight: 700,
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    letterSpacing: '0.5px',
  },
}));

const ProductImage = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#F8FAFC',
  border: '1px solid #E5E7EB',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.7rem',
  borderRadius: 6,
  height: 24,
  backgroundColor: status ? alpha(status, 0.12) : alpha(BRAND_COLORS.info, 0.12),
  color: status || BRAND_COLORS.info,
  '& .MuiChip-icon': {
    fontSize: 14,
    color: 'inherit',
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  backgroundColor: '#FFFFFF',
  borderRadius: 12,
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: alpha(BRAND_COLORS.primary, 0.25),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.95rem',
  color: BRAND_COLORS.text.primary,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    color: BRAND_COLORS.primary,
    fontSize: 20,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    transition: 'all 0.3s ease',
    fontSize: '0.9rem',
    fontWeight: 500,
    '&:hover fieldset': {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
      boxShadow: `0 0 0 4px ${alpha(BRAND_COLORS.primary, 0.1)}`,
    },
    '&.Mui-error fieldset': {
      borderColor: BRAND_COLORS.error,
    },
  },
  '& .MuiFormLabel-root': {
    fontWeight: 600,
    color: BRAND_COLORS.text.secondary,
    fontSize: '0.85rem',
    '&.Mui-focused': {
      color: BRAND_COLORS.primary,
    },
  },
  '& .MuiInputBase-input': {
    fontWeight: 500,
    padding: '12px 14px',
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: 10,
  backgroundColor: '#FFFFFF',
  minWidth: 150,
  fontWeight: 500,
  fontSize: '0.9rem',
  '& .MuiSelect-select': {
    padding: '12px 14px',
    fontWeight: 500,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: BRAND_COLORS.primary,
    borderWidth: 2,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: BRAND_COLORS.primary,
    borderWidth: 2,
    boxShadow: `0 0 0 4px ${alpha(BRAND_COLORS.primary, 0.1)}`,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    minWidth: 150,
    fontWeight: 500,
    fontSize: '0.9rem',
    '&:hover fieldset': {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderColor: BRAND_COLORS.primary,
      borderWidth: 2,
      boxShadow: `0 0 0 4px ${alpha(BRAND_COLORS.primary, 0.1)}`,
    },
    '& .MuiSelect-select': {
      padding: '12px 14px',
      fontWeight: 500,
    },
  },
  '& .MuiFormLabel-root': {
    fontWeight: 600,
    color: BRAND_COLORS.text.secondary,
    fontSize: '0.85rem',
    '&.Mui-focused': {
      color: BRAND_COLORS.primary,
    },
  },
}));

// ============================================
// CUSTOM HOOKS
// ============================================

const useAuth = () => {
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      logger.error('Error parsing user_data', e);
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'supplier';
  const accessToken = localStorage.getItem('access_token');

  return { userData, userType, accessToken };
};

// ============================================
// EDIT PRODUCT DIALOG
// ============================================

const EditProductDialog = ({ open, onClose, product, onUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (product && open) {
      setFormData({
        id: product.id,
        chemicalName: product.name || '',
        CASNumber: product.registryNumber || '',
        description: product.description || '',
        applications: product.applications || '',
        productType: product.productType || '',
        hazardClassifications: product.hazardClassifications || '',
        Synonyms: product.Synonyms || product.synonyms || '',
        Formula: product.ChemicalFormula || product.chemicalFormula || '',
        Grade: product.Grade || product.grade || '',
        otherGrade: product.otherGrade || '',
        ManufacturerName: product.ManufacturerName || product.manufacturerName || '',
        Country_ofOrigin: product.CountryOrigin || product.countryOrigin || '',
        hs_code: product.hs_code || product.hsCode || '', // ✅ NEW
        Certifications: product.Certifications || product.certifications || '',
        DeliveryTime: product.DeliveryTime || product.deliveryTime || '',
        price: product.price || '',
        Currency: product.currency || 'NGN',
        Discounts: product.discounts || 0,
        EnvironmentalImpact: product.EnvironmentalImpact || product.environmentalImpact || '',
        LifecycleDisposal: product.LifecycleDisposal || product.lifecycleDisposal || '',
        MSDS: product.msds || product.MSDS || '',
        HealthRisks: product.HealthRisks || product.healthRisks || '',
        status: product.status || 'active',
      });
      setErrors({});
      setTouched({});
      setActiveStep(0);
    }
  }, [product, open]);

  const validateStep = useCallback((step) => {
    const newErrors = {};
    const newTouched = {};

    switch (step) {
      case 0:
        if (!formData.chemicalName?.trim()) {
          newErrors.chemicalName = 'Chemical Name is required';
          newTouched.chemicalName = true;
        }
        if (!formData.CASNumber?.trim()) {
          newErrors.CASNumber = 'CAS Number is required';
          newTouched.CASNumber = true;
        }
        if (!formData.description?.trim()) {
          newErrors.description = 'Description is required';
          newTouched.description = true;
        }
        if (!formData.applications) {
          newErrors.applications = 'Applications is required';
          newTouched.applications = true;
        }
        if (!formData.productType) {
          newErrors.productType = 'Product Type is required';
          newTouched.productType = true;
        }
        if (!formData.hazardClassifications) {
          newErrors.hazardClassifications = 'Hazard Classification is required';
          newTouched.hazardClassifications = true;
        }
        if (!formData.Formula?.trim()) {
          newErrors.Formula = 'Chemical Formula is required';
          newTouched.Formula = true;
        }
        if (!formData.Grade) {
          newErrors.Grade = 'Grade is required';
          newTouched.Grade = true;
        }
        if (formData.Grade === 'Others' && !formData.otherGrade?.trim()) {
          newErrors.otherGrade = 'Please specify the grade';
          newTouched.otherGrade = true;
        }
        break;

      case 1:
        if (!formData.ManufacturerName?.trim()) {
          newErrors.ManufacturerName = 'Manufacturer Name is required';
          newTouched.ManufacturerName = true;
        }
        if (!formData.Country_ofOrigin?.trim()) {
          newErrors.Country_ofOrigin = 'Country of Origin is required';
          newTouched.Country_ofOrigin = true;
        }
        if (!formData.Certifications) {
          newErrors.Certifications = 'Certifications is required';
          newTouched.Certifications = true;
        }
        if (!formData.DeliveryTime) {
          newErrors.DeliveryTime = 'Delivery Time is required';
          newTouched.DeliveryTime = true;
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
          newErrors.price = 'Valid price is required';
          newTouched.price = true;
        }
        if (!formData.Currency) {
          newErrors.Currency = 'Currency is required';
          newTouched.Currency = true;
        }
        // ✅ NEW: Optional HS Code format validation
        if (formData.hs_code && formData.hs_code.trim() !== '' &&
            !/^\d{4}(\.\d{2}){0,2}$/.test(formData.hs_code.trim())) {
          newErrors.hs_code = 'HS Code must be in format like 2807 or 2807.00';
          newTouched.hs_code = true;
        }
        break;

      case 2:
        if (!formData.EnvironmentalImpact?.trim()) {
          newErrors.EnvironmentalImpact = 'Environmental Impact is required';
          newTouched.EnvironmentalImpact = true;
        }
        if (!formData.LifecycleDisposal?.trim()) {
          newErrors.LifecycleDisposal = 'Lifecycle & Disposal is required';
          newTouched.LifecycleDisposal = true;
        }
        if (!formData.MSDS?.trim()) {
          newErrors.MSDS = 'MSDS information is required';
          newTouched.MSDS = true;
        }
        if (!formData.HealthRisks?.trim()) {
          newErrors.HealthRisks = 'Health Risks is required';
          newTouched.HealthRisks = true;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    setTouched((prev) => ({ ...prev, ...newTouched }));
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateStep(activeStep);
  }, [activeStep, validateStep]);

  const handleNext = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, 2));
    }
  }, [activeStep, validateStep]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(2)) return;

    setSubmitting(true);
    logger.info('Updating product:', formData.id);

    try {
      const updateData = {
        id: formData.id,
        name: formData.chemicalName,
        registryNumber: formData.CASNumber,
        description: formData.description,
        applications: formData.applications,
        productType: formData.productType,
        hazardClassifications: formData.hazardClassifications,
        Synonyms: formData.Synonyms,
        ChemicalFormula: formData.Formula,
        Grade: formData.Grade,
        otherGrade: formData.otherGrade,
        ManufacturerName: formData.ManufacturerName,
        CountryOrigin: formData.Country_ofOrigin,
        hs_code: formData.hs_code, // ✅ NEW
        Certifications: formData.Certifications,
        DeliveryTime: formData.DeliveryTime,
        price: parseFloat(formData.price),
        currency: formData.Currency,
        discounts: parseFloat(formData.Discounts) || 0,
        EnvironmentalImpact: formData.EnvironmentalImpact,
        LifecycleDisposal: formData.LifecycleDisposal,
        msds: formData.MSDS,
        HealthRisks: formData.HealthRisks,
        status: formData.status,
      };

      const response = await fetch(API_ENDPOINTS.PRODUCTS.UPDATE(formData.id), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      const result = await response.json();
      logger.debug('Update response:', result);

      if (result.success) {
        const updatedProduct = { ...product, ...updateData };
        onUpdate(updatedProduct);
        onClose();
        logger.info('Product updated successfully');
      } else {
        throw new Error(result.message || 'Failed to update product');
      }
    } catch (error) {
      logger.error('Error updating product:', error);
      setErrors({ submit: error.message || 'Failed to update product' });
    } finally {
      setSubmitting(false);
    }
  }, [formData, product, onUpdate, onClose, validateStep]);

  const steps = ['Product Info', 'Supply Chain', 'Safety & Compliance'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 20,
          maxHeight: '90vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <EditIcon sx={{ color: BRAND_COLORS.primary }} />
          <Typography variant="h6" fontWeight={700}>
            Edit Product
          </Typography>
          <Chip
            label={`ID: ${formData.id || 'N/A'}`}
            size="small"
            sx={{ bgcolor: alpha(BRAND_COLORS.primary, 0.08), color: BRAND_COLORS.primary }}
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
          <Stepper activeStep={activeStep} orientation={isMobile ? 'vertical' : 'horizontal'} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          {activeStep === 0 && (
            <Fade in={true} timeout={300}>
              <Box>
                <FormSection>
                  <SectionTitle variant="h6">
                    <DescriptionIcon />
                    Basic Information
                  </SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Chemical Name"
                        name="chemicalName"
                        value={formData.chemicalName || ''}
                        onChange={(e) => handleChange('chemicalName', e.target.value)}
                        onBlur={() => handleBlur('chemicalName')}
                        error={!!errors.chemicalName && touched?.chemicalName}
                        helperText={touched?.chemicalName && errors.chemicalName}
                        placeholder="Enter chemical name"
                        required
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="CAS Number"
                        name="CASNumber"
                        value={formData.CASNumber || ''}
                        onChange={(e) => handleChange('CASNumber', e.target.value)}
                        onBlur={() => handleBlur('CASNumber')}
                        error={!!errors.CASNumber && touched?.CASNumber}
                        helperText={touched?.CASNumber && errors.CASNumber}
                        placeholder="e.g., 7647-01-0"
                        required
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description || ''}
                        onChange={(e) => handleChange('description', e.target.value)}
                        onBlur={() => handleBlur('description')}
                        error={!!errors.description && touched?.description}
                        helperText={touched?.description && errors.description}
                        placeholder="Enter product description..."
                        multiline
                        rows={2}
                        required
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledFormControl fullWidth error={!!errors.applications && touched?.applications} required size="small">
                        <InputLabel>Applications</InputLabel>
                        <StyledSelect
                          name="applications"
                          value={formData.applications || ''}
                          onChange={(e) => handleChange('applications', e.target.value)}
                          onBlur={() => handleBlur('applications')}
                          label="Applications"
                        >
                          <MenuItem value="" disabled>Select application</MenuItem>
                          {APPLICATIONS.map((app) => (
                            <MenuItem key={app} value={app}>{app}</MenuItem>
                          ))}
                        </StyledSelect>
                        {touched?.applications && errors.applications && (
                          <FormHelperText>{errors.applications}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledFormControl fullWidth error={!!errors.productType && touched?.productType} required size="small">
                        <InputLabel>Product Type</InputLabel>
                        <StyledSelect
                          name="productType"
                          value={formData.productType || ''}
                          onChange={(e) => handleChange('productType', e.target.value)}
                          onBlur={() => handleBlur('productType')}
                          label="Product Type"
                        >
                          <MenuItem value="" disabled>Select product type</MenuItem>
                          {PRODUCT_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </StyledSelect>
                        {touched?.productType && errors.productType && (
                          <FormHelperText>{errors.productType}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledFormControl fullWidth error={!!errors.hazardClassifications && touched?.hazardClassifications} required size="small">
                        <InputLabel>Hazard Classification</InputLabel>
                        <StyledSelect
                          name="hazardClassifications"
                          value={formData.hazardClassifications || ''}
                          onChange={(e) => handleChange('hazardClassifications', e.target.value)}
                          onBlur={() => handleBlur('hazardClassifications')}
                          label="Hazard Classification"
                        >
                          <MenuItem value="" disabled>Select hazard classification</MenuItem>
                          {HAZARD_CLASSIFICATIONS.map((hazard) => (
                            <MenuItem key={hazard} value={hazard}>{hazard}</MenuItem>
                          ))}
                        </StyledSelect>
                        {touched?.hazardClassifications && errors.hazardClassifications && (
                          <FormHelperText>{errors.hazardClassifications}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Chemical Formula"
                        name="Formula"
                        value={formData.Formula || ''}
                        onChange={(e) => handleChange('Formula', e.target.value)}
                        onBlur={() => handleBlur('Formula')}
                        error={!!errors.Formula && touched?.Formula}
                        helperText={touched?.Formula && errors.Formula}
                        placeholder="e.g., H2SO4"
                        required
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Synonyms"
                        name="Synonyms"
                        value={formData.Synonyms || ''}
                        onChange={(e) => handleChange('Synonyms', e.target.value)}
                        placeholder="Enter synonyms (comma separated)"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledFormControl fullWidth error={!!errors.Grade && touched?.Grade} required size="small">
                        <InputLabel>Grade</InputLabel>
                        <StyledSelect
                          name="Grade"
                          value={formData.Grade || ''}
                          onChange={(e) => handleChange('Grade', e.target.value)}
                          onBlur={() => handleBlur('Grade')}
                          label="Grade"
                        >
                          <MenuItem value="" disabled>Select Grade</MenuItem>
                          {GRADES.map((grade) => (
                            <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                          ))}
                        </StyledSelect>
                        {touched?.Grade && errors.Grade && (
                          <FormHelperText>{errors.Grade}</FormHelperText>
                        )}
                      </StyledFormControl>
                      {formData.Grade === 'Others' && (
                        <StyledTextField
                          fullWidth
                          sx={{ mt: 1 }}
                          label="Please specify grade"
                          name="otherGrade"
                          value={formData.otherGrade || ''}
                          onChange={(e) => handleChange('otherGrade', e.target.value)}
                          onBlur={() => handleBlur('otherGrade')}
                          error={!!errors.otherGrade && touched?.otherGrade}
                          helperText={touched?.otherGrade && errors.otherGrade}
                          placeholder="Enter grade specification"
                          required
                          size="small"
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledFormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <StyledSelect
                          name="status"
                          value={formData.status || 'active'}
                          onChange={(e) => handleChange('status', e.target.value)}
                          label="Status"
                        >
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="inactive">Inactive</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="draft">Draft</MenuItem>
                        </StyledSelect>
                      </StyledFormControl>
                    </Grid>
                  </Grid>
                </FormSection>
              </Box>
            </Fade>
          )}

          {activeStep === 1 && (
            <Fade in={true} timeout={300}>
              <Box>
                <FormSection>
                  <SectionTitle variant="h6">
                    <LocalShippingIcon />
                    Supply Chain Information
                  </SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Manufacturer Name"
                        name="ManufacturerName"
                        value={formData.ManufacturerName || ''}
                        onChange={(e) => handleChange('ManufacturerName', e.target.value)}
                        onBlur={() => handleBlur('ManufacturerName')}
                        error={!!errors.ManufacturerName && touched?.ManufacturerName}
                        helperText={touched?.ManufacturerName && errors.ManufacturerName}
                        placeholder="Enter manufacturer name"
                        required
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Country of Origin"
                        name="Country_ofOrigin"
                        value={formData.Country_ofOrigin || ''}
                        onChange={(e) => handleChange('Country_ofOrigin', e.target.value)}
                        onBlur={() => handleBlur('Country_ofOrigin')}
                        error={!!errors.Country_ofOrigin && touched?.Country_ofOrigin}
                        helperText={touched?.Country_ofOrigin && errors.Country_ofOrigin}
                        placeholder="Enter country of origin"
                        required
                        size="small"
                      />
                    </Grid>
                    {/* ✅ NEW: HS Code field in edit dialog */}
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="HS Code"
                        name="hs_code"
                        value={formData.hs_code || ''}
                        onChange={(e) => handleChange('hs_code', e.target.value)}
                        onBlur={() => handleBlur('hs_code')}
                        error={!!errors.hs_code && touched?.hs_code}
                        helperText={
                          (touched?.hs_code && errors.hs_code) ||
                          'Harmonized System Code (e.g., 2807.00)'
                        }
                        placeholder="e.g., 2807.00"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Chip
                                label="HS"
                                size="small"
                                sx={{
                                  bgcolor: alpha(BRAND_COLORS.primary, 0.1),
                                  color: BRAND_COLORS.primary,
                                  fontWeight: 700,
                                  fontSize: '0.6rem',
                                  height: 22,
                                  borderRadius: 4,
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledFormControl fullWidth error={!!errors.Certifications && touched?.Certifications} required size="small">
                        <InputLabel>Certifications</InputLabel>
                        <StyledSelect
                          name="Certifications"
                          value={formData.Certifications || ''}
                          onChange={(e) => handleChange('Certifications', e.target.value)}
                          onBlur={() => handleBlur('Certifications')}
                          label="Certifications"
                        >
                          <MenuItem value="" disabled>Select certifications</MenuItem>
                          {CERTIFICATIONS.map((cert) => (
                            <MenuItem key={cert} value={cert}>{cert}</MenuItem>
                          ))}
                        </StyledSelect>
                        {touched?.Certifications && errors.Certifications && (
                          <FormHelperText>{errors.Certifications}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledFormControl fullWidth error={!!errors.DeliveryTime && touched?.DeliveryTime} required size="small">
                        <InputLabel>Delivery Time</InputLabel>
                        <StyledSelect
                          name="DeliveryTime"
                          value={formData.DeliveryTime || ''}
                          onChange={(e) => handleChange('DeliveryTime', e.target.value)}
                          onBlur={() => handleBlur('DeliveryTime')}
                          label="Delivery Time"
                        >
                          <MenuItem value="" disabled>Select delivery time</MenuItem>
                          {DELIVERY_TIMES.map((time) => (
                            <MenuItem key={time} value={time}>{time}</MenuItem>
                          ))}
                        </StyledSelect>
                        {touched?.DeliveryTime && errors.DeliveryTime && (
                          <FormHelperText>{errors.DeliveryTime}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>
                  </Grid>
                </FormSection>

                <FormSection>
                  <SectionTitle variant="h6">
                    <MoneyIcon />
                    Pricing Information
                  </SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <StyledTextField
                        fullWidth
                        label="Price per Unit"
                        name="price"
                        type="number"
                        value={formData.price || ''}
                        onChange={(e) => handleChange('price', e.target.value)}
                        onBlur={() => handleBlur('price')}
                        error={!!errors.price && touched?.price}
                        helperText={touched?.price && errors.price}
                        placeholder="Enter price"
                        required
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography fontWeight={700} color="textSecondary">₦</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <StyledFormControl fullWidth error={!!errors.Currency && touched?.Currency} required size="small">
                        <InputLabel>Currency</InputLabel>
                        <StyledSelect
                          name="Currency"
                          value={formData.Currency || ''}
                          onChange={(e) => handleChange('Currency', e.target.value)}
                          onBlur={() => handleBlur('Currency')}
                          label="Currency"
                        >
                          <MenuItem value="" disabled>Select currency</MenuItem>
                          {CURRENCIES.map((curr) => (
                            <MenuItem key={curr} value={curr}>{curr}</MenuItem>
                          ))}
                        </StyledSelect>
                        {touched?.Currency && errors.Currency && (
                          <FormHelperText>{errors.Currency}</FormHelperText>
                        )}
                      </StyledFormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <StyledTextField
                        fullWidth
                        label="Discount %"
                        name="Discounts"
                        type="number"
                        value={formData.Discounts || ''}
                        onChange={(e) => handleChange('Discounts', e.target.value)}
                        placeholder="Enter discount if any"
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography fontWeight={700} color="textSecondary">%</Typography>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </FormSection>
              </Box>
            </Fade>
          )}

          {activeStep === 2 && (
            <Fade in={true} timeout={300}>
              <Box>
                <FormSection>
                  <SectionTitle variant="h6">
                    <SecurityIcon />
                    Environmental & Safety Information
                  </SectionTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Environmental Impact"
                        name="EnvironmentalImpact"
                        value={formData.EnvironmentalImpact || ''}
                        onChange={(e) => handleChange('EnvironmentalImpact', e.target.value)}
                        onBlur={() => handleBlur('EnvironmentalImpact')}
                        error={!!errors.EnvironmentalImpact && touched?.EnvironmentalImpact}
                        helperText={touched?.EnvironmentalImpact && errors.EnvironmentalImpact}
                        placeholder="Describe environmental impact"
                        required
                        multiline
                        rows={2}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Lifecycle & Disposal"
                        name="LifecycleDisposal"
                        value={formData.LifecycleDisposal || ''}
                        onChange={(e) => handleChange('LifecycleDisposal', e.target.value)}
                        onBlur={() => handleBlur('LifecycleDisposal')}
                        error={!!errors.LifecycleDisposal && touched?.LifecycleDisposal}
                        helperText={touched?.LifecycleDisposal && errors.LifecycleDisposal}
                        placeholder="Enter lifecycle and disposal advice"
                        required
                        multiline
                        rows={2}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="MSDS (Safety Data Sheet)"
                        name="MSDS"
                        value={formData.MSDS || ''}
                        onChange={(e) => handleChange('MSDS', e.target.value)}
                        onBlur={() => handleBlur('MSDS')}
                        error={!!errors.MSDS && touched?.MSDS}
                        helperText={touched?.MSDS && errors.MSDS}
                        placeholder="Enter handling, storage, and safety advice"
                        required
                        multiline
                        rows={2}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StyledTextField
                        fullWidth
                        label="Health Risks"
                        name="HealthRisks"
                        value={formData.HealthRisks || ''}
                        onChange={(e) => handleChange('HealthRisks', e.target.value)}
                        onBlur={() => handleBlur('HealthRisks')}
                        error={!!errors.HealthRisks && touched?.HealthRisks}
                        helperText={touched?.HealthRisks && errors.HealthRisks}
                        placeholder="Enter potential hazards to human health"
                        required
                        multiline
                        rows={2}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </FormSection>

                {errors.submit && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {errors.submit}
                  </Alert>
                )}
              </Box>
            </Fade>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: '1px solid #E5E7EB',
          justifyContent: 'space-between',
          bgcolor: '#F8FAFC',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={onClose}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: BRAND_COLORS.text.primary,
            }}
          >
            Cancel
          </Button>
          {activeStep === 2 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: BRAND_COLORS.primary,
                '&:hover': {
                  bgcolor: BRAND_COLORS.primaryDark,
                },
              }}
            >
              {submitting ? 'Updating...' : 'Update Product'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: BRAND_COLORS.primary,
                '&:hover': {
                  bgcolor: BRAND_COLORS.primaryDark,
                },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// PRODUCT CARD VIEW
// ============================================

const ProductCard = React.memo(({ product, onEdit, onDelete, onView }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    handleMenuClose();
    action();
  };

  const statusColor = STATUS_COLORS[product.status] || BRAND_COLORS.info;
  const imageUrl = getImageUrl(product.image);
  const hsCode = getHsCode(product); // ✅ NEW

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 48px ${alpha(BRAND_COLORS.primary, 0.08)}`,
        },
      }}
    >
      <CardHeader
        avatar={
          <ProductImage>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={product.name}
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                  logger.debug('Image load failed:', imageUrl);
                }}
                onLoad={() => logger.debug('Image loaded:', imageUrl)}
              />
            ) : (
              <ImageIcon sx={{ color: '#D1D5DB', fontSize: 24 }} />
            )}
          </ProductImage>
        }
        action={
          <IconButton onClick={handleMenuOpen} size="small" aria-label="product menu">
            <MoreVertIcon />
          </IconButton>
        }
        title={
          <Tooltip title={product.name}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {product.name}
            </Typography>
          </Tooltip>
        }
        subheader={
          <Typography variant="caption" color="textSecondary">
            CAS: {product.registryNumber || 'N/A'}
          </Typography>
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ flexGrow: 1, pt: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          <Chip
            label={product.productType || 'Uncategorized'}
            size="small"
            sx={{
              fontSize: '0.6rem',
              height: 20,
              bgcolor: alpha(BRAND_COLORS.primary, 0.08),
              color: BRAND_COLORS.primary,
            }}
          />
          {product.Grade && (
            <Chip
              label={product.Grade}
              size="small"
              sx={{
                fontSize: '0.6rem',
                height: 20,
                bgcolor: alpha(BRAND_COLORS.info, 0.08),
                color: BRAND_COLORS.info,
              }}
            />
          )}
          {/* ✅ NEW: HS Code chip on card */}
          {hsCode && (
            <Chip
              label={`HS: ${hsCode}`}
              size="small"
              sx={{
                fontSize: '0.6rem',
                height: 20,
                bgcolor: alpha(BRAND_COLORS.warning, 0.1),
                color: BRAND_COLORS.warning,
              }}
            />
          )}
          <StatusChip
            label={STATUS_LABELS[product.status] || 'Active'}
            status={statusColor}
            size="small"
          />
        </Box>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '0.85rem',
            mb: 1,
          }}
        >
          {product.description || 'No description available'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={800} color={BRAND_COLORS.primary}>
              {formatCurrency(product.price, product.currency)}
            </Typography>
            {product.discounts > 0 && (
              <Typography variant="caption" color="error" sx={{ textDecoration: 'line-through', display: 'block' }}>
                {formatCurrency(product.discountedPrice, product.currency)}
              </Typography>
            )}
          </Box>
          <Chip
            label={product.applications || 'Various'}
            size="small"
            sx={{
              fontSize: '0.6rem',
              height: 20,
              bgcolor: alpha(BRAND_COLORS.success, 0.08),
              color: BRAND_COLORS.success,
            }}
          />
        </Box>
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewIcon />}
          onClick={() => onView(product)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            flex: 1,
            borderColor: '#D1D5DB',
            color: BRAND_COLORS.text.primary,
            '&:hover': {
              borderColor: BRAND_COLORS.primary,
              color: BRAND_COLORS.primary,
            },
          }}
        >
          View
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => onEdit(product)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            flex: 1,
            bgcolor: BRAND_COLORS.primary,
            '&:hover': {
              bgcolor: BRAND_COLORS.primaryDark,
            },
          }}
        >
          Edit
        </Button>
      </CardActions>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            minWidth: 180,
          },
        }}
      >
        <MenuItem onClick={() => handleAction(() => onView(product))}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(() => onEdit(product))}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Product</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleAction(() => onDelete(product))} sx={{ color: BRAND_COLORS.error }}>
          <ListItemIcon sx={{ color: BRAND_COLORS.error }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
});

ProductCard.displayName = 'ProductCard';

// ============================================
// MAIN COMPONENT
// ============================================

const ProductManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));

  // ============================================
  // AUTH
  // ============================================
  const { userData, userType, accessToken } = useAuth();

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('products');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [filterStatus, setFilterStatus] = useState('all');

  const [showStockManagement, setShowStockManagement] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // UI states
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notifications] = useState([]);

  // ============================================
  // MENU ITEMS
  // ============================================
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'products', label: 'Products', icon: <InventoryIcon /> },
    { id: 'orders', label: 'Orders', icon: <ShoppingCartIcon /> },
    { id: 'registration', label: 'Register Product', icon: <VerifiedIcon /> },
    { id: 'analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
  ];

  // ============================================
  // API FUNCTIONS
  // ============================================

// In ProductManagement.jsx - FIXED fetchProducts function

const fetchProducts = useCallback(async () => {
  setLoading(true);
  logger.info('Fetching products...');

  try {
    // Get the logged-in user's ID from localStorage
    const userDataStr = localStorage.getItem('user_data');
    if (!userDataStr) {
      throw new Error('User data not found. Please login again.');
    }

    const userData = JSON.parse(userDataStr);
    const userId = userData.id;

    if (!userId) {
      throw new Error('User ID not found. Please login again.');
    }

    // Build URL with supplier_id filter
    // The supplier_id in products table references users.id
    const url = `${API_ENDPOINTS.PRODUCTS.LIST}?supplier_id=${userId}`;
    logger.info(`Fetching products for supplier ID: ${userId}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    logger.debug('Products response:', result);

    if (result.success) {
      let productsData = [];

      if (Array.isArray(result.data)) {
        productsData = result.data;
      } else if (result.data && typeof result.data === 'object') {
        if (Array.isArray(result.data.data)) {
          productsData = result.data.data;
        } else if (Array.isArray(result.data.products)) {
          productsData = result.data.products;
        } else {
          const arrayKey = Object.keys(result.data).find((key) => Array.isArray(result.data[key]));
          if (arrayKey) {
            productsData = result.data[arrayKey];
          }
        }
      } else if (Array.isArray(result)) {
        productsData = result;
      }

      setProducts(productsData);
      setFilteredProducts(productsData);
      logger.info(`Loaded ${productsData.length} products for supplier ${userId}`);
    } else {
      throw new Error(result.message || 'Failed to fetch products');
    }
  } catch (error) {
    logger.error('Error fetching products:', error);
    setSnackbar({
      open: true,
      message: error.message || 'Failed to load products. Please try again.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
}, [accessToken]);

  const deleteProduct = useCallback(
    async (productId) => {
      logger.info(`Deleting product ID: ${productId}`);

      try {
        const response = await fetch(API_ENDPOINTS.PRODUCTS.DELETE(productId), {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: productId }),
        });

        const result = await response.json();
        logger.debug('Delete response:', result);

        if (result.success) {
          setProducts((prev) => prev.filter((p) => p.id !== productId));
          setFilteredProducts((prev) => prev.filter((p) => p.id !== productId));
          setSnackbar({
            open: true,
            message: 'Product deleted successfully',
            severity: 'success',
          });
          logger.info(`Product ${productId} deleted successfully`);
        } else {
          throw new Error(result.message || 'Failed to delete product');
        }
      } catch (error) {
        logger.error('Error deleting product:', error);
        setSnackbar({
          open: true,
          message: error.message || 'Failed to delete product',
          severity: 'error',
        });
      }
    },
    [accessToken]
  );

  const updateProduct = useCallback((updatedProduct) => {
    setProducts((prev) => prev.map((p) => (p.id === updatedProduct.id ? { ...p, ...updatedProduct } : p)));
    setFilteredProducts((prev) => prev.map((p) => (p.id === updatedProduct.id ? { ...p, ...updatedProduct } : p)));
    setSnackbar({
      open: true,
      message: 'Product updated successfully',
      severity: 'success',
    });
    logger.info(`Product ${updatedProduct.id} updated successfully`);
  }, []);

  // ============================================
  // HANDLERS
  // ============================================
// ============================================
// NAVIGATION HANDLER
// ============================================
const handleSidebarNavigation = useCallback((path) => {
  // Update the selected tab state
  setSelectedTab(path);
  // Navigate to the path
  navigate(path);
  // Close drawer on mobile
  if (isMobile) setDrawerOpen(false);
}, [navigate, isMobile]);


 
  const handleLogout = useCallback(() => {
    logger.info('Logging out user');
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleChangePassword = useCallback(() => {
    setProfileAnchorEl(null);
    setSnackbar({ open: true, message: 'Password change dialog would open', severity: 'info' });
    logger.debug('Change password requested');
  }, []);

  const handleSearch = useCallback(
    (event) => {
      const term = event.target.value.toLowerCase();
      setSearchTerm(term);

      if (!term.trim()) {
        setFilteredProducts(products);
        return;
      }

      const filtered = products.filter(
        (product) =>
          product.name?.toLowerCase().includes(term) ||
          product.registryNumber?.toLowerCase().includes(term) ||
          product.description?.toLowerCase().includes(term) ||
          product.productType?.toLowerCase().includes(term) ||
          product.applications?.toLowerCase().includes(term) ||
          product.manufacturerName?.toLowerCase().includes(term) ||
          // ✅ NEW: search HS Code too
          (product.hs_code && product.hs_code.toLowerCase().includes(term))
      );

      setFilteredProducts(filtered);
      logger.debug(`Search results: ${filtered.length} products found`);
    },
    [products]
  );

  const handleFilterStatus = useCallback(
    (status) => {
      setFilterStatus(status);
      if (status === 'all') {
        setFilteredProducts(products);
      } else {
        const filtered = products.filter((p) => p.status === status);
        setFilteredProducts(filtered);
      }
      logger.debug(`Filtered by status: ${status}`);
    },
    [products]
  );

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleEdit = useCallback((product) => {
    logger.info(`Editing product: ${product.id}`);
    setSelectedProduct(product);
    setEditDialogOpen(true);
  }, []);

  const handleView = useCallback((product) => {
    logger.info(`Viewing product: ${product.id}`);
    setSelectedProduct(product);
    setViewDialogOpen(true);
  }, []);

  const handleDelete = useCallback((product) => {
    logger.info(`Delete product: ${product.id}`);
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (selectedProduct) {
      await deleteProduct(selectedProduct.id);
      setDeleteDialogOpen(false);
      setSelectedProduct(null);
    }
  }, [selectedProduct, deleteProduct]);

  const handleAddProduct = useCallback(() => {
    logger.info('Navigating to product registration');
    navigate('/supplier/ProductRegistration');
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    fetchProducts();
    setSnackbar({
      open: true,
      message: 'Products refreshed',
      severity: 'info',
    });
  }, [fetchProducts]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setFilteredProducts(products);
  }, [products]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || userType !== 'supplier') {
      logger.warn('Unauthorized access, redirecting to auth');
      navigate('/RawAuth');
      return;
    }
    fetchProducts();
  }, [fetchProducts, navigate, userType]);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const paginatedProducts = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProducts.slice(start, start + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  const totalProducts = useMemo(() => filteredProducts.length, [filteredProducts]);

  const statusCounts = useMemo(() => {
    const counts = { all: products.length };
    products.forEach((p) => {
      const status = p.status || 'active';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [products]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={`skeleton-${index}`}>
          <StyledTableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="rounded" width={48} height={48} />
              <Box>
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={80} height={16} />
              </Box>
            </Box>
          </StyledTableCell>
          <StyledTableCell>
            <Skeleton variant="text" width={100} height={20} />
          </StyledTableCell>
          <StyledTableCell>
            <Skeleton variant="rounded" width={80} height={24} />
          </StyledTableCell>
          <StyledTableCell align="right">
            <Skeleton variant="text" width={80} height={20} />
          </StyledTableCell>
          <StyledTableCell>
            <Skeleton variant="rounded" width={60} height={24} />
          </StyledTableCell>
          <StyledTableCell align="center">
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </StyledTableCell>
        </TableRow>
      ))}
    </>
  );

  const renderTable = () => (
    <TableContainer>
      <Table sx={{ minWidth: 800 }}>
        <TableHead>
          <TableRow>
            <StyledTableCell>Product</StyledTableCell>
            <StyledTableCell>CAS Number</StyledTableCell>
            <StyledTableCell>HS Code</StyledTableCell>
            <StyledTableCell>Type</StyledTableCell>
            <StyledTableCell align="right">Price</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell align="center">Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            renderSkeleton()
          ) : paginatedProducts.length === 0 ? (
            <TableRow>
              <StyledTableCell colSpan={7} align="center" sx={{ py: 8 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <InventoryIcon sx={{ fontSize: 48, color: '#D1D5DB' }} />
                  <Typography variant="h6" color="textSecondary" fontWeight={600}>
                    No products found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {searchTerm ? 'Try adjusting your search or filters' : 'Get started by registering your first product'}
                  </Typography>
                  {!searchTerm && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProduct} sx={{ mt: 1 }}>
                      Register Product
                    </Button>
                  )}
                </Box>
              </StyledTableCell>
            </TableRow>
          ) : (
            paginatedProducts.map((product) => {
              const statusColor = STATUS_COLORS[product.status] || BRAND_COLORS.info;
              const imageUrl = getImageUrl(product.image);
              const hsCode = getHsCode(product); // ✅ NEW

              return (
                <TableRow
                  key={product.id}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(BRAND_COLORS.primary, 0.02),
                    },
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ProductImage>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={product.name}
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              logger.debug('Image load failed:', imageUrl);
                            }}
                          />
                        ) : (
                          <ImageIcon sx={{ color: '#D1D5DB', fontSize: 20 }} />
                        )}
                      </ProductImage>
                      <Box>
                        <Tooltip title={product.name}>
                          <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 150 }}>
                            {product.name}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="textSecondary">
                          {product.manufacturerName || product.ManufacturerName || 'Unknown Manufacturer'}
                        </Typography>
                      </Box>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2" fontWeight={500} fontFamily="monospace">
                      {product.registryNumber || 'N/A'}
                    </Typography>
                  </StyledTableCell>
                  {/* ✅ NEW: HS Code column */}
                  <StyledTableCell>
                    <Typography variant="body2" fontWeight={500} fontFamily="monospace">
                      {hsCode || 'N/A'}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={product.productType || 'N/A'}
                      size="small"
                      sx={{
                        fontSize: '0.6rem',
                        height: 20,
                        bgcolor: alpha(BRAND_COLORS.primary, 0.08),
                        color: BRAND_COLORS.primary,
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Typography variant="body2" fontWeight={700}>
                      {formatCurrency(product.price, product.currency)}
                    </Typography>
                    {product.discounts > 0 && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ textDecoration: 'line-through', display: 'block' }}
                      >
                        {formatCurrency(product.discountedPrice, product.currency)}
                      </Typography>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <StatusChip
                      label={STATUS_LABELS[product.status] || 'Active'}
                      status={statusColor}
                      size="small"
                    />
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => handleView(product)} sx={{ color: BRAND_COLORS.info }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(product)} sx={{ color: BRAND_COLORS.primary }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(product)} sx={{ color: BRAND_COLORS.error }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </StyledTableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderGrid = () => (
    <Grid container spacing={3}>
      {loading ? (
        [...Array(6)].map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${index}`}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                avatar={<Skeleton variant="rounded" width={48} height={48} />}
                title={<Skeleton variant="text" width="80%" />}
                subheader={<Skeleton variant="text" width="60%" />}
              />
              <CardContent>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="80%" />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Skeleton variant="text" width="30%" />
                  <Skeleton variant="rounded" width={60} height={20} />
                </Box>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                <Skeleton variant="rounded" width="100%" height={36} />
                <Skeleton variant="rounded" width="100%" height={36} />
              </CardActions>
            </Card>
          </Grid>
        ))
      ) : paginatedProducts.length === 0 ? (
        <Grid item xs={12}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <InventoryIcon sx={{ fontSize: 48, color: '#D1D5DB' }} />
            <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
              No products found
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProduct} sx={{ mt: 2 }}>
              Register Product
            </Button>
          </Box>
        </Grid>
      ) : (
        paginatedProducts.map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <ProductCard
              product={product}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
            />
          </Grid>
        ))
      )}
    </Grid>
  );

  // ============================================
  // RENDER
  // ============================================

  if (loading && !products.length) {
    return <Preloader text="Loading Products..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F0F2F5' }}>
    <Sidebar
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  isMobile={isMobile}
  selectedTab={selectedTab}
  onTabChange={handleSidebarNavigation}  // ← Use the handler instead of setSelectedTab
  userData={userData}
  onLogout={() => {
    localStorage.clear();
    navigate('/RawAuth');
  }}
/>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={menuItems}
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={notifications}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={handleChangePassword}
         
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 0 } }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    color: BRAND_COLORS.text.primary,
                  }}
                >
                  Product Management
                </Typography>
                <Typography variant="body1" color="textSecondary" fontWeight={500}>
                  {totalProducts} products registered
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={handleRefresh} sx={{ bgcolor: '#FFFFFF', boxShadow: 1 }}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddProduct}
                  sx={{
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: BRAND_COLORS.primary,
                    '&:hover': {
                      bgcolor: BRAND_COLORS.primaryDark,
                    },
                  }}
                >
                  Add Product
                </Button>

                
<Button
  variant="outlined"
  startIcon={<WarehouseIcon />}
  onClick={() => setShowStockManagement(!showStockManagement)}
  sx={{
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 700,
    borderColor: BRAND_COLORS.primary,
    color: BRAND_COLORS.primary,
    '&:hover': {
      borderColor: BRAND_COLORS.primaryDark,
      bgcolor: alpha(BRAND_COLORS.primary, 0.04),
    },
  }}
>
  {showStockManagement ? 'View Products' : 'Manage Stock'}
</Button>
              </Box>
            </Box>

            {/* Filters */}
            <StyledCard sx={{ mb: 3 }}>
              <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <TextField
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={handleSearch}
                  size="small"
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 200px' },
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <Select
                    value={filterStatus}
                    onChange={(e) => handleFilterStatus(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: 3 }}
                  >
                    <MenuItem value="all">All Status ({statusCounts.all})</MenuItem>
                    {Object.entries(PRODUCT_STATUSES).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {STATUS_LABELS[value] || value} ({statusCounts[value] || 0})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ ml: { xs: 0, sm: 'auto' }, display: 'flex', gap: 1 }}>
                  <Tooltip title="Table View">
                    <IconButton
                      size="small"
                      onClick={() => setViewMode('table')}
                      sx={{
                        bgcolor: viewMode === 'table' ? alpha(BRAND_COLORS.primary, 0.1) : 'transparent',
                        color: viewMode === 'table' ? BRAND_COLORS.primary : '#9CA3AF',
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Grid View">
                    <IconButton
                      size="small"
                      onClick={() => setViewMode('grid')}
                      sx={{
                        bgcolor: viewMode === 'grid' ? alpha(BRAND_COLORS.primary, 0.1) : 'transparent',
                        color: viewMode === 'grid' ? BRAND_COLORS.primary : '#9CA3AF',
                      }}
                    >
                      <InventoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </StyledCard>

            {/* Products */}
         {/* Products - Conditional Rendering for Stock Management */}
{showStockManagement ? (
  <StyledCard>
    <StockManagement
      products={products}
      onStockUpdate={updateProduct}
      onRefresh={fetchProducts}
      accessToken={accessToken}
      loading={loading}
    />
  </StyledCard>
) : (
  <StyledCard>
    {viewMode === 'table' ? renderTable() : renderGrid()}

    {totalProducts > 0 && (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderTop: '1px solid #E5E7EB',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalProducts)} of{' '}
          {totalProducts} products
        </Typography>
        <TablePagination
          component="div"
          count={totalProducts}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          sx={{
            '& .MuiToolbar-root': {
              px: 0,
              flexWrap: 'wrap',
              justifyContent: 'center',
            },
          }}
        />
      </Box>
    )}
  </StyledCard>
)}
          </Container>
        </Box>

        <Footer />
      </Box>

      {/* ============================================
      EDIT PRODUCT DIALOG
      ============================================ */}
      <EditProductDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onUpdate={updateProduct}
      />

      {/* ============================================
      DELETE DIALOG
      ============================================ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 20,
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: alpha(BRAND_COLORS.error, 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DeleteIcon sx={{ color: BRAND_COLORS.error, fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Delete Product
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
            Are you sure you want to delete this product?
          </Typography>
          {selectedProduct && (
            <Alert severity="warning" sx={{ borderRadius: 2, mt: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                {selectedProduct.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                CAS: {selectedProduct.registryNumber || 'N/A'}
              </Typography>
              {getHsCode(selectedProduct) && (
                <Typography variant="caption" color="textSecondary" display="block">
                  HS: {getHsCode(selectedProduct)}
                </Typography>
              )}
            </Alert>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: BRAND_COLORS.text.primary,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================
      VIEW DIALOG
      ============================================ */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 20,
            overflow: 'hidden',
          },
        }}
      >
        {selectedProduct && (
          <>
            <DialogTitle sx={{ bgcolor: '#F8FAFC', borderBottom: '1px solid #E5E7EB' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ProductImage sx={{ width: 56, height: 56 }}>
                    {getImageUrl(selectedProduct.image) ? (
                      <img
                        src={getImageUrl(selectedProduct.image)}
                        alt={selectedProduct.name}
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          logger.debug('Image load failed:', getImageUrl(selectedProduct.image));
                        }}
                      />
                    ) : (
                      <ImageIcon sx={{ color: '#D1D5DB', fontSize: 28 }} />
                    )}
                  </ProductImage>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      {selectedProduct.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" fontFamily="monospace">
                      CAS: {selectedProduct.registryNumber || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setViewDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" fontWeight={600} gutterBottom>
                    Basic Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Description
                      </Typography>
                      <Typography variant="body2">{selectedProduct.description || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Product Type
                      </Typography>
                      <Chip
                        label={selectedProduct.productType || 'N/A'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Applications
                      </Typography>
                      <Chip
                        label={selectedProduct.applications || 'N/A'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Hazard Classification
                      </Typography>
                      <Chip
                        label={selectedProduct.hazardClassifications || 'N/A'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Chemical Formula
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {selectedProduct.ChemicalFormula || selectedProduct.chemicalFormula || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Synonyms
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.Synonyms || selectedProduct.synonyms || 'No synonyms available'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Grade
                      </Typography>
                      <Typography variant="body2">{selectedProduct.Grade || selectedProduct.grade || 'N/A'}</Typography>
                    </Box>
                    {selectedProduct.otherGrade && (
                      <Box>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Other Grade
                        </Typography>
                        <Typography variant="body2">{selectedProduct.otherGrade}</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Supply Chain */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" fontWeight={600} gutterBottom>
                    Supply Chain
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Manufacturer
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.ManufacturerName || selectedProduct.manufacturerName || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Country of Origin
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.CountryOrigin || selectedProduct.countryOrigin || 'N/A'}
                      </Typography>
                    </Box>
                    {/* ✅ NEW: HS Code in view dialog */}
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        HS Code
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                        {getHsCode(selectedProduct) || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Certifications
                      </Typography>
                      <Chip
                        label={selectedProduct.Certifications || selectedProduct.certifications || 'N/A'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Delivery Time
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.DeliveryTime || selectedProduct.deliveryTime || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Status
                      </Typography>
                      <StatusChip
                        label={STATUS_LABELS[selectedProduct.status] || 'Active'}
                        status={STATUS_COLORS[selectedProduct.status] || BRAND_COLORS.info}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Pricing */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" fontWeight={600} gutterBottom>
                    Pricing
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Price
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color={BRAND_COLORS.primary}>
                        {formatCurrency(selectedProduct.price, selectedProduct.currency)}
                      </Typography>
                    </Box>
                    {selectedProduct.discounts > 0 && (
                      <Box>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Discount
                        </Typography>
                        <Chip
                          label={`${selectedProduct.discounts}% off`}
                          size="small"
                          color="error"
                          sx={{ mt: 0.5 }}
                        />
                        <Typography variant="body2" color="textSecondary">
                          Discounted Price: {formatCurrency(selectedProduct.discountedPrice, selectedProduct.currency)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Safety & Compliance */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" fontWeight={600} gutterBottom>
                    Safety & Compliance
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Environmental Impact
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.EnvironmentalImpact || selectedProduct.environmentalImpact || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Lifecycle & Disposal
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.LifecycleDisposal || selectedProduct.lifecycleDisposal || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        MSDS
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedProduct.msds || selectedProduct.MSDS || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Health Risks
                      </Typography>
                      <Typography variant="body2">
                        {selectedProduct.HealthRisks || selectedProduct.healthRisks || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB', gap: 1, flexWrap: 'wrap' }}>
              <Button
                onClick={() => setViewDialogOpen(false)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setViewDialogOpen(false);
                  handleEdit(selectedProduct);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: BRAND_COLORS.primary,
                  '&:hover': {
                    bgcolor: BRAND_COLORS.primaryDark,
                  },
                }}
              >
                Edit Product
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ============================================
      SNACKBAR
      ============================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          icon={snackbar.severity === 'success' ? <CheckIcon /> : <WarningIcon />}
          sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            fontWeight: 600,
            '& .MuiAlert-message': {
              fontWeight: 600,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ============================================
      NOTIFICATIONS POPOVER
      ============================================ */}
      <Popover
        open={Boolean(notifAnchorEl)}
        anchorEl={notifAnchorEl}
        onClose={() => setNotifAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 340,
            maxHeight: 400,
            borderRadius: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid #E5E7EB',
          },
        }}
      >
        <Box sx={{ p: 2.5, borderBottom: '1px solid #E5E7EB' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
        </Box>
        <Box sx={{ overflow: 'auto', p: 2.5 }}>
          <Typography variant="body2" color="textSecondary" fontWeight={500} textAlign="center">
            No notifications
          </Typography>
        </Box>
      </Popover>

      {/* ============================================
      PROFILE MENU
      ============================================ */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={() => setProfileAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 220,
            borderRadius: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid #E5E7EB',
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
          
            setProfileAnchorEl(null);
          }}
          sx={{
            fontWeight: 500,
            borderRadius: 1,
            '&:hover': { bgcolor: alpha(BRAND_COLORS.primary, 0.04) },
          }}
        >
          <PersonIcon fontSize="small" sx={{ mr: 1.5, color: BRAND_COLORS.primary }} />
          Profile
        </MenuItem>
        <MenuItem
          onClick={handleChangePassword}
          sx={{
            fontWeight: 500,
            borderRadius: 1,
            '&:hover': { bgcolor: alpha(BRAND_COLORS.primary, 0.04) },
          }}
        >
          <LockOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: BRAND_COLORS.primary }} />
          Change Password
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{
            color: BRAND_COLORS.error,
            fontWeight: 600,
            borderRadius: 1,
            '&:hover': { bgcolor: alpha(BRAND_COLORS.error, 0.04) },
          }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: BRAND_COLORS.error }} />
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProductManagement;